import {
    ContractLinks,
    ContractStatus,
    ContractType,
    EncryptedContractNumber,
    FinanceContract as FinanceContractType,
} from 'cpes-common';
import { ContractActionItemType, ContractActions, useAnalyticsActionTracker } from '@cp-shared-19/frontend-ui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSelector } from 'hooks/useTypedSelector';
import { setAccordionOpenItems } from '../AccordionOpenItemsSlice';
import { AdditionalProducts } from '../additional-products/AdditionalProducts';
import { ContractParties } from '../contract-parties';
import { FinanceFinancialDetails } from '../financial-details';
import { VehicleDetails } from '../vehicle-details';
import {
    amortizationTablePath,
    earlySettlementPagePath,
    requestCertificatePath,
    postboxPagePath,
} from '../../navigation/paths';
import { UnpaidDetailsAlert } from 'components/unpaid-details/unpaid-details-alert';
import { currentBrand } from 'config';
import { Accordion, Card } from '@vwfs-bronson/bronson-react';
import { LicensePlate } from '../../license-plate';
import { FinanceContractHeaderSummary } from './FinanceContractHeaderSummary';
import { getProductCategoryTranslationLabel } from '../ProductCategoryTranslationLabel';
import { useHistory } from 'react-router-dom';
import { PostboxLocationState } from 'components/postbox/PostboxLocationState';

export type FinanceContractProps = {
    contract: FinanceContractType & EncryptedContractNumber;
    links?: ContractLinks;
    /* eslint-disable-next-line @typescript-eslint/ban-types */
    getViewChange: Function;
    defaultExpanded?: boolean;
};

export const FinanceContract: React.FC<FinanceContractProps> = ({
    contract,
    links,
    getViewChange,
    defaultExpanded,
}) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const openItems = useSelector(state => state.accordionOpenItems.openItems);
    const { t } = useTranslation('contracts');
    const { onAction: trackOnAccordionItemOpen } = useAnalyticsActionTracker('onOpenContractAccordion');
    const { onAction: trackOnContractActionEntryPointClick } = useAnalyticsActionTracker(
        'onContractActionEntryPointClick',
    );
    const redirectTo = (path: string) => {
        history.push(path);
    };

    const {
        carInformation,
        contractNumber,
        contractStatus,
        contractType,
        _encryptedContractNumber,
        licensePlateNumber,
        unpaidAmount,
        unpaidInstalments,
        nextInstalmentDate,
        nextInstalmentAmount,
        totalAmount,
    } = contract;

    const isFinanceLeaseContract = contractType === ContractType.FINANCE_LEASE;

    const contractCategory =
        contractType === ContractType.FINANCING
            ? t(getProductCategoryTranslationLabel(ContractType.FINANCING))
            : t(getProductCategoryTranslationLabel(ContractType.FINANCE_LEASE));

    const contractActionItems: ContractActionItemType[] = [
        {
            iconName: 'semantic-file',
            onClick: () => {
                redirectTo(requestCertificatePath(_encryptedContractNumber));
                trackOnContractActionEntryPointClick(t('contract-actions.request-certificate'));
            },
            label: t('contract-actions.request-certificate'),
        },
    ];

    if (!isFinanceLeaseContract) {
        contractActionItems.push({
            iconName: 'semantic-calendar',
            onClick: () => {
                redirectTo(amortizationTablePath(_encryptedContractNumber));
                trackOnContractActionEntryPointClick(t('contract-actions.amortization-table'));
            },
            label: t('contract-actions.amortization-table'),
        });
    }

    if (links?.totalEarlySettlement && !isFinanceLeaseContract) {
        contractActionItems.push({
            iconName: 'semantic-calculator',
            onClick: () => {
                redirectTo(earlySettlementPagePath(_encryptedContractNumber));
                trackOnContractActionEntryPointClick(t('contract-actions.early-settlement'));
            },
            label: t('contract-actions.early-settlement'),
        });
    }

    if (isFinanceLeaseContract) {
        contractActionItems.push({
            iconName: 'semantic-file',
            onClick: () => {
                const invoicesLocationState: PostboxLocationState = {
                    filterToInvoices: true,
                    timeframeFilter: 'LAST_3_MONTHS',
                };
                history.push(postboxPagePath(contractNumber), invoicesLocationState);
                trackOnContractActionEntryPointClick(t('contract-actions.invoices'));
            },
            label: t('contract-actions.invoices'),
        });
    }

    const licensePlateComponent = <LicensePlate licensePlateNumber={licensePlateNumber} />;

    const isUnpaidPresent = unpaidAmount && unpaidAmount > 0.0 && unpaidInstalments && unpaidInstalments > 0;

    const contractBody = links ? (
        <div data-theme={currentBrand === 'seat' ? 'alternative' : 'primary'}>
            <Accordion
                lazyRender
                openItems={openItems}
                onChange={(items: string[]): void => {
                    dispatch(setAccordionOpenItems(items));
                    const newItem = items.find(item => !openItems.includes(item));
                    if (newItem) {
                        const viewChange = getViewChange(newItem);
                        trackOnAccordionItemOpen(viewChange);
                    }
                }}
            >
                <>
                    <div>
                        <ContractActions contractActionItems={contractActionItems} />
                    </div>
                    {links.financialDetails && (
                        <FinanceFinancialDetails
                            hasUnpaidBalance={!!isUnpaidPresent && contractStatus === ContractStatus.ACTIVE}
                            contractId={contractNumber}
                            encryptedContractId={_encryptedContractNumber}
                            link={links.financialDetails}
                            isFinanceLeaseContract={isFinanceLeaseContract}
                        />
                    )}
                    {links.additionalProducts && (
                        <AdditionalProducts
                            contractId={contractNumber}
                            isFinanceLeaseContract={isFinanceLeaseContract}
                            link={links.additionalProducts}
                        />
                    )}
                    {links.vehicleDetails && <VehicleDetails contractId={contractNumber} link={links.vehicleDetails} />}
                    {links.contractParties && (
                        <ContractParties contractId={contractNumber} link={links.contractParties} />
                    )}
                </>
            </Accordion>
        </div>
    ) : null;

    const contractUnpaids = isUnpaidPresent ? (
        <UnpaidDetailsAlert
            encryptedContractId={_encryptedContractNumber}
            unpaidAmount={unpaidAmount}
            className="u-mt u-mb-small"
        />
    ) : null;

    return (
        <Card
            expandable
            title={carInformation}
            titleLevel={3}
            subtitle={`${contractCategory} ${contractNumber}`}
            contentShort={licensePlateComponent}
            className={'u-mb'}
            contentLarge={contractBody}
            defaultExpanded={defaultExpanded}
        >
            <FinanceContractHeaderSummary
                nextInstalmentDate={nextInstalmentDate}
                nextInstalmentAmount={nextInstalmentAmount}
                totalAmount={totalAmount}
            />
            {contractUnpaids}
        </Card>
    );
};
